import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';

import { Asset, ContentfulCollection, EntryFields } from 'contentful';
import get from 'lodash/get';

import { AccessLevel } from '../../enums/access-level';
import { BusinessContext, NavigationDropdown, NavigationGroup, NavigationItem } from '../../models/config';
import {
  TypeBlogPost,
  TypeBrand,
  TypeBrandSubgroup,
  TypeCaseStudy,
  TypeCountry,
  TypeCountryLocale,
  TypeDynamicPage,
  TypeFeature,
  TypeFiller,
  TypeFooterCountryInfo,
  TypeGlobalSection,
  TypeHeader,
  TypeIndexPage,
  TypeIndustry,
  TypeLabelIconPair,
  TypeLandingPage,
  TypeLanguages,
  TypeNavigationDropdown,
  TypeNavigationGroup,
  TypeNavigationItem,
  TypePolymerGroup,
  TypePolymerSubgroup,
  TypeProcess,
  TypeProducer,
  TypeProduct,
  TypeProductFiller,
  TypeQuality,
  TypeSalesTermsAndConditions,
  TypeSavedSection,
  TypeSectionTemplate,
  TypeSegment,
  TypeSeoMetadata,
  TypeSpecification,
  TypeSpecificationProvider,
  TypeSustainabilityGroup,
  TypeSustainabilitySubgroup,
  TypeSystemPage,
  TypeTechnicalProperty
} from '../../models/contentful-generated';
import {
  NormalizedAsset,
  NormalizedBlogPost,
  NormalizedBrand,
  NormalizedBrandSubgroup,
  NormalizedBusinessContext,
  NormalizedCaseStudy,
  NormalizedCountry,
  NormalizedCountryLanguagePair,
  NormalizedCountryLocale,
  NormalizedDynamicPage,
  NormalizedFeature,
  NormalizedFiller,
  NormalizedGlobalSection,
  NormalizedHeader,
  NormalizedIndexPage,
  NormalizedLabelIconPair,
  NormalizedLanguage,
  NormalizedLocalizedSalesTermCondition,
  NormalizedMarket,
  NormalizedNavigationDropdown,
  NormalizedNavigationGroup,
  NormalizedNavigationItem,
  NormalizedOle,
  NormalizedPage,
  NormalizedPolymerGroup,
  NormalizedPolymerSubgroup,
  NormalizedProducer,
  NormalizedProduct,
  NormalizedProductFiller,
  NormalizedQuality,
  NormalizedSalesTermCondition,
  NormalizedSavedSection,
  NormalizedSectionTemplate,
  NormalizedSegment,
  NormalizedSeoMetadata,
  NormalizedSpecification,
  NormalizedSpecificationProvider,
  NormalizedSustainabilityGroup,
  NormalizedSustainabilitySubgroup,
  NormalizedSystemPage,
  NormalizedTechnicalProperty,
  NormalizedTechnology
} from '../../models/contentful-mapped';
import { ElasticBrand, ElasticCaseStudy, ElasticProduct } from '../../models/elastic';
import { BrandCard, CaseStudyCard, ProductCard } from '../../models/elements';

/**
 * This Utility class acts as an abstraction layer between Contentful and our apps.
 * Map Contentful responses to application-specific data structures.
 */
export class DataMapperUtils {
  /**
   * Type guard to check if a value is a RichText Document
   *
   * @param value - Value to check
   * @returns True if the value is a RichText Document
   */
  private static isRichTextDocument(value: unknown): value is EntryFields.RichText {
    return typeof value === 'object' && value !== null && 'nodeType' in value && 'content' in value;
  }

  /**
   * Convert legacy text (string) to RichText format for backward compatibility.
   * If the input is already RichText, return as is. If it's a string, wrap it in a basic RichText document.
   *
   * @param textOrRichText - Either a plain text string (legacy) or RichText document (new)
   * @returns RichText document or null
   */
  private static convertToRichText(textOrRichText: EntryFields.Text | EntryFields.RichText | undefined): EntryFields.RichText | null {
    if (!textOrRichText) {
      return null;
    }

    // If it's already a RichText document, return as is (TypeScript now knows the type!)
    if (DataMapperUtils.isRichTextDocument(textOrRichText)) {
      return textOrRichText;
    }

    // convert to RichText
    return {
      nodeType: BLOCKS.DOCUMENT,
      data: {},
      content: [
        {
          nodeType: BLOCKS.PARAGRAPH,
          data: {},
          marks: [],
          content: [
            {
              nodeType: 'text',
              value: textOrRichText,
              marks: [],
              data: {}
            }
          ]
        }
      ]
    };
  }

  /**
   * Normalize a Contentful BlogPost to a NormalizedBlogPost
   *
   * @param blogPost
   */
  static normalizeContentfulBlogPost(blogPost: TypeBlogPost): NormalizedBlogPost {
    return {
      id: blogPost.sys.id,
      contentType: blogPost.sys.contentType.sys.id,
      title: blogPost.fields.title,
      slug: blogPost.fields.slug,
      route: blogPost.fields.slug,
      category: blogPost.fields.category,
      excerpt: blogPost.fields.excerpt,
      image: DataMapperUtils.normalizeContentfulAsset(blogPost.fields.image),
      author: blogPost.fields.author,
      date: new Date(blogPost.fields.date).toLocaleString('en', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }),
      pageContent: blogPost.fields.pageContent ?? null,
      translations: blogPost.fields.translations ?? null,
      seoMetadata: blogPost.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(blogPost.fields.seoMetadata) : null
    };
  }

  /**
   * Normalize a Contentful Asset to a NormalizedAsset
   *
   * @param asset
   */
  static normalizeContentfulAsset(asset: Asset): NormalizedAsset {
    return {
      id: asset.sys.id,
      contentType: 'asset',
      title: asset.fields.title,
      description: asset.fields.description,
      url: asset.fields.file.url,
      width: asset.fields.file.details.image?.width ?? null,
      height: asset.fields.file.details.image?.height ?? null
    };
  }

  /**
   * Normalize a Contentful Segment to a NormalizedSegment
   *
   * @param segment
   */
  static normalizeContentfulSegment(segment: TypeSegment): NormalizedSegment {
    return {
      id: segment.sys.id,
      contentType: segment.sys.contentType.sys.id,
      name: segment.fields.name,
      description: segment.fields.description ? documentToReactComponents(segment.fields.description as Document) : null,
      applications: segment.fields.applications || [],
      image: segment.fields.image ? DataMapperUtils.normalizeContentfulAsset(segment.fields.image) : null,
      market: segment.fields.industry ? DataMapperUtils.normalizeContentfulMarket(segment.fields.industry) : null,
      rank: segment.fields.rank
    };
  }

  /**
   * Normalize a Contentful Product to a NormalizedProduct
   *
   * @param product
   */
  static normalizeContentfulProduct(product: TypeProduct): NormalizedProduct {
    return {
      id: product.sys.id,
      contentType: product.sys.contentType.sys.id,
      name: product.fields.name,
      slug: product.fields.slug,
      grade: product.fields.grade ?? null,
      description: DataMapperUtils.convertToRichText(product.fields.description),
      ranking: product.fields.ranking ?? null,
      image: product.fields.image ? DataMapperUtils.normalizeContentfulAsset(product.fields.image) : null,
      brand: DataMapperUtils.normalizeContentfulBrand(product.fields.brand),
      polymerGroup: product.fields.polymerSubgroup?.fields.group?.fields.name ?? null,
      polymerSubGroup: product.fields.polymerSubgroup?.fields.name ?? null,
      marketSegments: product.fields.industrySegments
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.industrySegments, DataMapperUtils.normalizeContentfulSegment)
        : [],
      fillers: product.fields.fillers
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.fillers, DataMapperUtils.normalizeContentfulProductFiller)
        : [],
      technologies: product.fields.processes
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.processes, DataMapperUtils.normalizeContentfulTechnology)
        : [],
      features: product.fields.features
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.features, DataMapperUtils.normalizeContentfulFeature)
        : [],
      specifications: product.fields.specifications
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.specifications, DataMapperUtils.normalizeContentfulSpecification)
        : [],
      form: product.fields.form?.fields.name ?? null,
      preferred: product.fields.isPreferred ?? false,
      quality: product.fields.quality ? DataMapperUtils.normalizeContentfulQuality(product.fields.quality) : null,
      ulPortfolio: product.fields.ulPortfolio ?? false,
      ulProductId: product.fields.ulProductId ?? null,
      modifiedAt: product.fields.modifiedAt ? new Date(product.fields.modifiedAt) : null,
      sustainability: product.fields.sustainability ? DataMapperUtils.normalizeContentfulSustainabilitySubgroup(product.fields.sustainability) : null,
      density: product.fields.density
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.density, DataMapperUtils.normalizeContentfulTechnicalProperty)
        : [],
      flowRate: product.fields.flowRate
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.flowRate, DataMapperUtils.normalizeContentfulTechnicalProperty)
        : [],
      flameRating: product.fields.flameRating
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.flameRating, DataMapperUtils.normalizeContentfulTechnicalProperty)
        : [],
      notchedImpact: product.fields.notchedImpact
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.notchedImpact, DataMapperUtils.normalizeContentfulTechnicalProperty)
        : [],
      notchedImpactStrength: product.fields.notchedImpactStrength
        ? DataMapperUtils.normalizeContentfulCollection(product.fields.notchedImpactStrength, DataMapperUtils.normalizeContentfulTechnicalProperty)
        : [],
      seoMetadata: product.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(product.fields.seoMetadata) : null,
      isTailCatalog: product.fields.isTailCatalog ?? false
    };
  }

  /**
   * Normalize a Contentful CaseStudy to a NormalizedCaseStudy
   *
   * @param caseStudy
   */
  static normalizeContentfulCaseStudy(caseStudy: TypeCaseStudy): NormalizedCaseStudy {
    return {
      id: caseStudy.sys.id,
      contentType: caseStudy.sys.contentType.sys.id,
      visibility: caseStudy.fields.visibility ?? null,
      slug: caseStudy.fields.slug,
      title: caseStudy.fields.title,
      excerpt: caseStudy.fields.excerpt ?? null,
      functionKeyRequirements: caseStudy.fields.functionKeyRequirements ?? null,
      benefits: caseStudy.fields.benefits ?? null,
      link: caseStudy.fields.link ?? null,
      image: caseStudy.fields.images?.length ? DataMapperUtils.normalizeContentfulAsset(caseStudy.fields.images[0]) : null,
      segments: caseStudy.fields.segments
        ? DataMapperUtils.normalizeContentfulCollection(caseStudy.fields.segments, DataMapperUtils.normalizeContentfulSegment)
        : [],
      processingMethod: caseStudy.fields.processingMethod ? DataMapperUtils.normalizeContentfulTechnology(caseStudy.fields.processingMethod) : null,
      products: caseStudy.fields.products
        ? DataMapperUtils.normalizeContentfulCollection(caseStudy.fields.products, DataMapperUtils.normalizeContentfulProduct)
        : [],
      features: caseStudy.fields.features
        ? DataMapperUtils.normalizeContentfulCollection(caseStudy.fields.features, DataMapperUtils.normalizeContentfulFeature)
        : [],
      images: caseStudy.fields.images
        ? DataMapperUtils.normalizeContentfulCollection(caseStudy.fields.images, DataMapperUtils.normalizeContentfulAsset)
        : [],
      confidential: caseStudy.fields.confidential ?? false,
      seoMetadata: caseStudy.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(caseStudy.fields.seoMetadata) : null,
      type: caseStudy.fields.type ?? null,
      productionCountry: caseStudy.fields.productionCountry ? DataMapperUtils.normalizeContentfulCountry(caseStudy.fields.productionCountry) : null
    };
  }

  /**
   * Normalize a Contentful Technology to a NormalizedTechnology
   *
   * @param technology
   */
  static normalizeContentfulTechnology(technology: TypeProcess): NormalizedTechnology {
    return {
      id: technology.sys.id,
      contentType: technology.sys.contentType.sys.id,
      slug: technology.fields.slug,
      name: technology.fields.name ?? null,
      shortDescription: technology.fields.shortDescription ?? null,
      description: technology.fields.description ?? null,
      image: technology.fields.image ? DataMapperUtils.normalizeContentfulAsset(technology.fields.image) : null,
      seoMetadata: technology.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(technology.fields.seoMetadata) : null
    };
  }

  /**
   * Normalize a Contentful Producer to a NormalizedProducer
   *
   * @param producer
   */
  static normalizeContentfulProducer(producer: TypeProducer): NormalizedProducer {
    return {
      id: producer.sys.id,
      contentType: producer.sys.contentType.sys.id,
      name: producer.fields.name,
      logo: producer.fields.logo ? DataMapperUtils.normalizeContentfulAsset(producer.fields.logo) : null,
      slug: producer.fields.slug,
      storefront: producer.fields.storefront
        ? {
            id: producer.fields.storefront.sys.id,
            contentType: producer.fields.storefront.sys.contentType.sys.id,
            route: producer.fields.storefront.fields.route ?? null,
            seoMetadata: producer.fields.storefront.fields.seoMetadata
              ? DataMapperUtils.normalizeContentfulSeoMetadata(producer.fields.storefront.fields.seoMetadata)
              : null
          }
        : null,
      seoMetadata: producer.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(producer.fields.seoMetadata) : null
    };
  }

  /**
   * Normalize a Contentful Market to a NormalizedMarket
   *
   * @param market
   */
  static normalizeContentfulMarket(market: TypeIndustry): NormalizedMarket {
    return {
      id: market.sys.id,
      contentType: market.sys.contentType.sys.id,
      name: market.fields.name ?? null,
      description: market.fields.description ?? null,
      shortDescription: market.fields.shortDescription ?? null,
      slug: market.fields.slug,
      keyAreas: market.fields.keyAreas ?? [],
      callToActionType: market.fields.callToActionType ?? 'Off',
      image: market.fields.image ? DataMapperUtils.normalizeContentfulAsset(market.fields.image) : null,
      seoMetadata: market.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(market.fields.seoMetadata) : null
    };
  }

  /**
   * Normalize a Contentful Brand to a NormalizedBrand
   *
   * @param brand
   */
  static normalizeContentfulBrand(brand: TypeBrand): NormalizedBrand {
    return {
      id: brand.sys.id,
      contentType: brand.sys.contentType.sys.id,
      slug: brand.fields.slug,
      name: brand.fields.name,
      excerpt: brand.fields.excerpt ?? null,
      brandDescription: DataMapperUtils.convertToRichText(brand.fields.brandDescription),
      producer: DataMapperUtils.normalizeContentfulProducer(brand.fields.producer),
      countries: brand.fields.countries
        ? DataMapperUtils.normalizeContentfulCollection(brand.fields.countries, DataMapperUtils.normalizeContentfulCountry)
        : [],
      brandSubgroup: DataMapperUtils.normalizeContentfulCollection(brand.fields.brandSubgroup, DataMapperUtils.normalizeContentfulBrandSubgroup),
      seoMetadata: brand.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(brand.fields.seoMetadata) : null,
      brandBannerImage: brand.fields.brandBannerImage ? DataMapperUtils.normalizeContentfulAsset(brand.fields.brandBannerImage) : null
    };
  }

  /**
   * Normalize a Contentful BrandSubgroup to a NormalizedBrandSubgroup
   *
   * @param brandSubgroup
   */
  static normalizeContentfulBrandSubgroup(brandSubgroup: TypeBrandSubgroup): NormalizedBrandSubgroup {
    return {
      id: brandSubgroup.sys.id,
      contentType: brandSubgroup.sys.contentType.sys.id,
      polymerSubgroup: DataMapperUtils.normalizeContentfulCollection(
        brandSubgroup.fields.polymerSubgroup,
        DataMapperUtils.normalizeContentfulPolymerSubgroup
      )
    };
  }

  /**
   * Normalize a Contentful PolymerSubgroup to a NormalizedPolymerSubgroup
   *
   * @param polymerSubgroup
   */
  static normalizeContentfulPolymerSubgroup(polymerSubgroup: TypePolymerSubgroup): NormalizedPolymerSubgroup {
    return {
      id: polymerSubgroup.sys.id,
      contentType: polymerSubgroup.sys.contentType.sys.id,
      name: polymerSubgroup.fields.name ?? null,
      group: polymerSubgroup.fields.group ? DataMapperUtils.normalizeContentfulPolymerGroup(polymerSubgroup.fields.group) : null
    };
  }

  /**
   * Normalize a Contentful PolymerGroup to a NormalizedPolymerGroup
   *
   * @param polymerGroup
   */
  static normalizeContentfulPolymerGroup(polymerGroup: TypePolymerGroup): NormalizedPolymerGroup {
    return {
      id: polymerGroup.sys.id,
      contentType: polymerGroup.sys.contentType.sys.id,
      name: polymerGroup.fields.name ?? null
    };
  }

  /**
   * Normalize a Contentful SeoMetadata to a NormalizedSeoMetadata
   *
   * @param seoMetadata
   */
  static normalizeContentfulSeoMetadata(seoMetadata: TypeSeoMetadata): NormalizedSeoMetadata {
    return {
      description: seoMetadata.fields.description,
      noFollow: !!seoMetadata.fields.noFollow
    };
  }

  /**
   * Normalize a Contentful Country to a NormalizedCountry
   *
   * @param country
   */
  static normalizeContentfulCountry(country: TypeCountry): NormalizedCountry {
    return {
      id: country.sys.id,
      contentType: country.sys.contentType.sys.id,
      name: country.fields.name,
      isoCode: country.fields.isoCode
    };
  }

  /**
   * Normalize a Contentful CountryLocale to a NormalizedCountryLocale
   *
   * @param countryLocale
   */
  static normalizeContentfulCountryLocale(countryLocale: TypeCountryLocale): NormalizedCountryLocale {
    return {
      id: countryLocale.sys.id,
      contentType: countryLocale.sys.contentType.sys.id,
      name: countryLocale.fields.fullName,
      isoCode: countryLocale.fields.shortName
    };
  }

  /**
   * Normalize a Contentful Language to a NormalizedLanguage
   *
   * @param language
   */
  static normalizeContentfulLanguage(language: TypeLanguages): NormalizedLanguage {
    return {
      id: language.sys.id,
      contentType: language.sys.contentType.sys.id,
      fullName: language.fields.fullName,
      shortName: language.fields.shortName
    };
  }

  /**
   * Normalize a Contentful ProductFiller to a NormalizedProductFiller
   *
   * @param productFiller
   */
  static normalizeContentfulProductFiller(productFiller: TypeProductFiller): NormalizedProductFiller {
    return {
      id: productFiller.sys.id,
      contentType: productFiller.sys.contentType.sys.id,
      filler: productFiller.fields.filler ? DataMapperUtils.normalizeContentfulFiller(productFiller.fields.filler) : null,
      percentage: productFiller.fields.percentage ?? null
    };
  }

  /**
   * Normalize a Contentful Filler to a NormalizedFiller
   *
   * @param filler
   */
  static normalizeContentfulFiller(filler: TypeFiller): NormalizedFiller {
    return {
      id: filler.sys.id,
      contentType: filler.sys.contentType.sys.id,
      name: filler.fields.name,
      percentage: filler.fields.percentage ?? null
    };
  }

  /**
   * Normalize a Contentful Feature to a NormalizedFeature
   *
   * @param feature
   */
  static normalizeContentfulFeature(feature: TypeFeature): NormalizedFeature {
    return {
      id: feature.sys.id,
      contentType: feature.sys.contentType.sys.id,
      name: feature.fields.name ?? null
    };
  }

  /**
   * Normalize a Contentful Specification to a NormalizedSpecification
   *
   * @param specification
   */
  static normalizeContentfulSpecification(specification: TypeSpecification): NormalizedSpecification {
    return {
      id: specification.sys.id,
      contentType: specification.sys.contentType.sys.id,
      name: specification.fields.name,
      variation: specification.fields.variation ?? null,
      provider: specification.fields.provider ? DataMapperUtils.normalizeContentfulSpecificationProvider(specification.fields.provider) : null
    };
  }

  /**
   * Normalize a Contentful SpecificationProvider to a NormalizedSpecificationProvider
   *
   * @param specificationProvider
   */
  static normalizeContentfulSpecificationProvider(specificationProvider: TypeSpecificationProvider): NormalizedSpecificationProvider {
    return {
      id: specificationProvider.sys.id,
      contentType: specificationProvider.sys.contentType.sys.id,
      name: specificationProvider.fields.name,
      type: specificationProvider.fields.type ?? null
    };
  }

  /**
   * Normalize a Contentful Quality to a NormalizedQuality
   *
   * @param quality
   */
  static normalizeContentfulQuality(quality: TypeQuality): NormalizedQuality {
    return {
      id: quality.sys.id,
      contentType: quality.sys.contentType.sys.id,
      name: quality.fields.name ?? null
    };
  }

  /**
   * Normalize a Contentful SustainabilitySubgroup to a NormalizedSustainabilitySubgroup
   *
   * @param sustainabilitySubgroup
   */
  static normalizeContentfulSustainabilitySubgroup(sustainabilitySubgroup: TypeSustainabilitySubgroup): NormalizedSustainabilitySubgroup {
    return {
      id: sustainabilitySubgroup.sys.id,
      contentType: sustainabilitySubgroup.sys.contentType.sys.id,
      name: sustainabilitySubgroup.fields.name,
      sustainabilityGroup: sustainabilitySubgroup.fields.sustainabilityGroup
        ? DataMapperUtils.normalizeContentfulSustainabilityGroup(sustainabilitySubgroup.fields.sustainabilityGroup)
        : null,
      labelIconPair: sustainabilitySubgroup.fields.labelIconPair
        ? DataMapperUtils.normalizeContentfulLabelIconPair(sustainabilitySubgroup.fields.labelIconPair)
        : null
    };
  }

  /**
   * Normalize a Contentful SustainabilityGroup to a NormalizedSustainabilityGroup
   *
   * @param sustainabilityGroup
   */
  static normalizeContentfulSustainabilityGroup(sustainabilityGroup: TypeSustainabilityGroup): NormalizedSustainabilityGroup {
    return {
      id: sustainabilityGroup.sys.id,
      contentType: sustainabilityGroup.sys.contentType.sys.id,
      name: sustainabilityGroup.fields.name
    };
  }

  /**
   * Normalize a Contentful LabelIconPair to a NormalizedLabelIconPair
   *
   * @param labelIconPair
   */
  static normalizeContentfulLabelIconPair(labelIconPair: TypeLabelIconPair): NormalizedLabelIconPair {
    return {
      id: labelIconPair.sys.id,
      contentType: labelIconPair.sys.contentType.sys.id,
      label: labelIconPair.fields.label ?? null,
      icon: labelIconPair.fields.icon ?? null
    };
  }

  /**
   * Normalize a Contentful Technical Property to a NormalizedTechnicalProperty
   *
   * @param techProp
   */
  static normalizeContentfulTechnicalProperty(techProp: TypeTechnicalProperty): NormalizedTechnicalProperty {
    return {
      id: techProp.sys.id,
      contentType: techProp.sys.contentType.sys.id,
      value: techProp.fields.value ?? '',
      testingMethod: techProp.fields.testingMethod,
      unit: techProp.fields.unit,
      condition: techProp.fields.condition ?? null
    };
  }

  /**
   * Normalize a Contentful salesTermCondition to a NormalizedSalesTermCondition
   *
   * @param salesTermCondition
   */
  static normalizeContentfulSalesTermCondition(salesTermCondition: TypeSalesTermsAndConditions): NormalizedSalesTermCondition {
    return {
      id: salesTermCondition.sys.id,
      contentType: salesTermCondition.sys.contentType.sys.id,
      title: salesTermCondition.fields.ole.fields.title,
      slug: salesTermCondition.fields.slug,
      content: salesTermCondition.fields.content ?? null,
      ole: DataMapperUtils.normalizeContentfulOle(salesTermCondition.fields.ole)
    };
  }

  /**
   * Normalize a localized Contentful salesTermCondition to a NormalizedLocalizedSalesTermCondition
   *
   * @param localizedSalesTermCondition
   */
  static normalizeContentfulLocalizedSalesTermCondition(localizedSalesTermCondition: any): NormalizedLocalizedSalesTermCondition {
    return {
      id: localizedSalesTermCondition.sys.id,
      contentType: localizedSalesTermCondition.sys.contentType.sys.id,
      title: localizedSalesTermCondition.fields.ole.en.fields.title.en,
      slug: localizedSalesTermCondition.fields.slug.en,
      content: localizedSalesTermCondition.fields.content ?? null,
      ole: DataMapperUtils.normalizeContentfulLocalizedOle(localizedSalesTermCondition.fields.ole)
    };
  }

  /**
   * Normalize a Contentful Ole to a NormalizedOle
   *
   * @param ole
   */
  static normalizeContentfulOle(ole: TypeFooterCountryInfo): NormalizedOle {
    return {
      id: ole.sys.id,
      contentType: ole.sys.contentType.sys.id,
      title: ole.fields.title,
      country: DataMapperUtils.normalizeContentfulCountryLocale(ole.fields.country),
      email: ole.fields.email,
      phone: ole.fields.phone,
      address: ole.fields.address as Document,
      oleId: ole.fields.oleId ?? null
    };
  }

  /**
   * Normalize a localized Contentful Ole to a NormalizedOle by language
   *
   * @param ole
   */
  static normalizeContentfulLocalizedOle(ole: any): { [key: string]: NormalizedOle } {
    const mappedOle: { [key: string]: NormalizedOle } = {};

    Object.entries(ole).forEach(([language, value]) => {
      mappedOle[language] = {
        id: (value as any).sys.id,
        contentType: (value as any).sys.contentType.sys.id,
        title: (value as any).fields.title?.[language] ?? (value as any).fields.title.en,
        country: DataMapperUtils.normalizeContentfulCountryLocale((value as any).fields.country?.[language] ?? (value as any).fields.country.en),
        email: (value as any).fields.email?.[language] ?? (value as any).fields.email.en,
        phone: (value as any).fields.phone?.[language] ?? (value as any).fields.phone.en,
        address: (value as any).fields.address?.[language] ?? (value as any).fields.address.en,
        oleId: (value as any).fields.oleId?.[language] ?? (value as any).fields.oleId?.en ?? null
      };
    });

    return mappedOle;
  }

  /**
   * Normalize a Contentful DynamicPage to a NormalizedDynamicPage
   *
   * @param dynamicPage
   */
  static normalizeContentfulDynamicPage(dynamicPage: TypeDynamicPage & { sys: { publishedVersion?: number } }): NormalizedDynamicPage {
    return {
      id: dynamicPage.sys.id,
      published: dynamicPage.sys.publishedVersion ?? null,
      internalName: dynamicPage.fields.internalName,
      contentType: dynamicPage.fields.contentType,
      parentPage: dynamicPage.fields.parentPage ? DataMapperUtils.normalizeContentfulLandingPage(dynamicPage.fields.parentPage) : null,
      route: dynamicPage.fields.route ?? null,
      slug: dynamicPage.fields.slug ?? null,
      routeIds: dynamicPage.fields.routeIds ?? [],
      accessType: dynamicPage.fields.accessType,
      theme: dynamicPage.fields.theme,
      pageContent: dynamicPage.fields.pageContent ?? [],
      translations: dynamicPage.fields.translations ?? null
    };
  }

  /**
   * Normalize a Contentful IndexPage to a NormalizedIndexPage
   *
   * @param indexPage
   */
  static normalizeContentfulIndexPage(indexPage: TypeIndexPage): NormalizedIndexPage {
    return {
      id: indexPage.sys.id,
      contentType: indexPage.sys.contentType.sys.id,
      internalName: indexPage.fields.internalName,
      accessType: indexPage.fields.accessType,
      theme: indexPage.fields.theme,
      seoMetadata: indexPage.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(indexPage.fields.seoMetadata) : null,
      pageContent: indexPage.fields.pageContent ?? [],
      translations: indexPage.fields.translations ?? null
    };
  }

  /**
   * Normalize a Contentful LandingPage to a NormalizedPage
   *
   * @param page
   */
  static normalizeContentfulLandingPage(page: TypeLandingPage & { sys: { publishedVersion?: number } }): NormalizedPage {
    return {
      id: page.sys.id,
      contentType: page.sys.contentType.sys.id,
      published: page.sys.publishedVersion ?? null,
      internalName: page.fields.internalName,
      parentPage: page.fields.parentPage ? DataMapperUtils.normalizeContentfulLandingPage(page.fields.parentPage) : null,
      route: page.fields.route ?? null,
      slug: page.fields.slug ?? null,
      routeIds: page.fields.routeIds ?? [],
      accessType: page.fields.accessType,
      theme: page.fields.theme,
      seoMetadata: page.fields.seoMetadata ? DataMapperUtils.normalizeContentfulSeoMetadata(page.fields.seoMetadata) : null,
      pageContent: page.fields.pageContent ?? [],
      translations: page.fields.translations ?? null
    };
  }

  /**
   * Normalize a Contentful SystemPage to a NormalizedSystemPage
   *
   * @param systemPage
   */
  static normalizeContentfulSystemPage(systemPage: TypeSystemPage): NormalizedSystemPage {
    return {
      id: systemPage.sys.id,
      contentType: systemPage.sys.contentType.sys.id,
      internalName: systemPage.fields.internalName,
      pageTitle: systemPage.fields.pageTitle,
      type: systemPage.fields.type,
      parentPage: systemPage.fields.parentPage ? DataMapperUtils.normalizeContentfulSystemPage(systemPage.fields.parentPage) : null,
      route: systemPage.fields.route ?? null,
      slug: systemPage.fields.slug ?? null,
      routeIds: systemPage.fields.routeIds ?? [],
      pageContent: systemPage.fields.pageContent ?? [],
      translations: systemPage.fields.translations ?? null
    };
  }

  /**
   * Normalize a Contentful GlobalSection to a NormalizedGlobalSection
   *
   * @param globalSection
   */
  static normalizeContentfulGlobalSection(globalSection: TypeGlobalSection): NormalizedGlobalSection {
    return {
      id: globalSection.sys.id,
      contentType: globalSection.sys.contentType.sys.id,
      internalName: globalSection.fields.internalName,
      pageContent: globalSection.fields.pageContent ?? null,
      accessType: globalSection.fields.accessType
    };
  }

  /**
   * Normalize a Contentful SavedSection to a NormalizedSavedSection
   *
   * @param savedSection
   */
  static normalizeContentfulSavedSection(savedSection: TypeSavedSection): NormalizedSavedSection {
    return {
      id: savedSection.sys.id,
      contentType: savedSection.sys.contentType.sys.id,
      label: savedSection.fields.label,
      category: savedSection.fields.category,
      pageContent: savedSection.fields.node ? [savedSection.fields.node] : []
    };
  }

  /**
   * Normalize a Contentful SectionTemplate to a NormalizedSectionTemplate
   *
   * @param sectionTemplate
   */
  static normalizeContentfulSectionTemplate(sectionTemplate: TypeSectionTemplate): NormalizedSectionTemplate {
    return {
      id: sectionTemplate.sys.id,
      contentType: sectionTemplate.sys.contentType.sys.id,
      internalName: sectionTemplate.fields.internalName,
      label: sectionTemplate.fields.internalName,
      accessType: sectionTemplate.fields.accessType,
      theme: sectionTemplate.fields.theme,
      category: sectionTemplate.fields.category,
      pageContent: sectionTemplate.fields.pageContent ?? [],
      translations: sectionTemplate.fields.translations ?? null
    };
  }

  /**
   * Normalize a NormalizedBrand to a BrandCard
   *
   * @param brand
   */
  static normalizeBrandCard(brand: NormalizedBrand, products?: Array<NormalizedProduct>): BrandCard {
    return {
      id: brand.id,
      slug: brand.slug,
      name: brand.name,
      excerpt: brand.excerpt,
      polymerTypes:
        products
          ?.filter((product) => product.polymerSubGroup)
          .map((product) => product.polymerSubGroup ?? '')
          .filter((polymerType, index, self) => polymerType && self.indexOf(polymerType) === index) ?? [],
      numberOfProducts: products?.length ?? 0,
      producer: {
        name: brand.producer?.name ?? null,
        logo: brand.producer?.logo?.url ?? null
      },
      noFollow: brand.seoMetadata?.noFollow ?? false
    };
  }

  /**
   * Normalize an ElasticBrand to a BrandCard
   *
   * @param brand
   */
  static normalizeElasticBrandCard(brand: ElasticBrand): BrandCard {
    return {
      id: brand.slug,
      slug: brand.slug,
      name: brand.name,
      excerpt: brand.excerpt,
      polymerTypes: brand.polymerTypes ?? [],
      numberOfProducts: brand.numberOfItems ?? 0,
      producer: {
        name: brand.producer?.name ?? null,
        logo: brand.producer?.logoUrl ?? null
      }
    };
  }

  /**
   * Normalize a NormalizedProduct to a ProductCard
   *
   * @param product
   */
  static normalizeProductCard(product: NormalizedProduct, assetBasePath?: string, acceptWebp?: boolean): ProductCard {
    const getImage = (): string => {
      if (product.image?.url) return product.image.url;

      const marketSegmentFallback = product.marketSegments?.[0]?.image?.url;

      if (marketSegmentFallback) return marketSegmentFallback;

      return `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;
    };

    return {
      id: product.id,
      slug: product.slug,
      name: product.name,
      producer: product.brand.producer?.name ?? null,
      polymerSubgroup: product.polymerSubGroup,
      fillers: product.fillers.map(({ filler, percentage }) => `${percentage ? `${percentage} ` : ''}${filler?.name}`) ?? [],
      technologies: product.technologies.map(({ name }) => name ?? '') ?? [],
      features: product.features.map((feature) => feature.name ?? '') ?? [],
      quality: product.quality?.name ?? null,
      isPreferred: product.preferred,
      image: getImage(),
      noFollow: false
    };
  }

  /**
   * Normalize an ElasticProduct to a ProductCard
   *
   * @param product
   */
  static normalizeElasticProductCard(product: ElasticProduct, assetBasePath?: string, acceptWebp?: boolean): ProductCard {
    const getImage = (): string => {
      if (product.imageUrl) return product.imageUrl;
      if (product.segments?.[0]?.imageUrl) return product.segments[0].imageUrl;

      if (product.industries?.length && product.industries.length > 0) {
        for (let i = 0; i < product.industries.length; i += 1) {
          const industry = product.industries[i];
          for (let j = 0; j < industry.segments.length; j += 1) {
            const segment = industry.segments[j];
            if (segment.imageUrl) return segment.imageUrl;
          }
        }
      }

      return `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;
    };

    return {
      id: product.id.toString(),
      slug: product.slug,
      name: product.name,
      producer: product.producer,
      polymerSubgroup: product.polymerSubGroup,
      fillers: product.fillers?.map(({ name, percentage }) => `${percentage !== undefined ? `${percentage}% ` : ''}${name}`) ?? [],
      technologies: product.processes?.map(({ name }) => name) ?? [],
      features: product.features ?? [],
      quality: product.quality,
      isPreferred: product.isPreferred,
      image: getImage()
    };
  }

  /**
   * Normalize a NormalizedCaseStudy to a CaseStudyCard
   *
   * @param caseStudy
   */
  static normalizeCaseStudyCard(caseStudy: NormalizedCaseStudy, assetBasePath?: string, acceptWebp?: boolean): CaseStudyCard {
    const getMostCommonMarket = (): NormalizedMarket | null => {
      const markets = caseStudy.segments.map((segment) => segment.market).filter((item) => item);

      if (!markets.length) return null;

      return markets.sort((a, b) => markets.filter((v) => v === a).length - markets.filter((v) => v === b).length).pop() ?? null;
    };

    const getImage = (): string => {
      const fallback = `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;

      if (caseStudy.image?.url) return caseStudy.image.url;
      if (caseStudy.images?.[0]?.url) return caseStudy.images[0].url;

      const mostCommonMarket = getMostCommonMarket();

      if (mostCommonMarket) return mostCommonMarket.image?.url ?? fallback;

      return fallback;
    };

    return {
      id: caseStudy.id,
      slug: caseStudy.slug,
      title: caseStudy.title,
      excerpt: caseStudy.excerpt,
      markets: caseStudy.segments.map(({ market }) => market?.name ?? ''),
      products: caseStudy.products.map((product) => ({
        name: product.name,
        pristine: false
      })),
      isConfidential: caseStudy.confidential,
      image: getImage(),
      backgroundColor: 'primary',
      noFollow: caseStudy.seoMetadata?.noFollow,
      visibility: caseStudy.visibility
    };
  }

  /**
   * Normalize an ElasticCaseStudy to a CaseStudyCard
   *
   * @param caseStudy
   */
  static normalizeElasticCaseStudyCard(caseStudy: ElasticCaseStudy, assetBasePath?: string, acceptWebp?: boolean): CaseStudyCard {
    const getImage = (): string => {
      if (caseStudy.images?.[0]) return caseStudy.images[0];

      if (caseStudy.industries.length > 0) {
        for (let i = 0; i < caseStudy.industries.length; i += 1) {
          const industry = caseStudy.industries[i];
          if (industry.imageUrl) {
            return industry.imageUrl;
          }
          for (let j = 0; j < industry.segments.length; j += 1) {
            const segment = industry.segments[j];
            if (segment.imageUrl) return segment.imageUrl;
          }
        }
      }

      return `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}`;
    };

    return {
      id: caseStudy.slug,
      slug: caseStudy.slug,
      title: caseStudy.label,
      excerpt: caseStudy.excerpt,
      markets: caseStudy.industries.map(({ name }) => name),
      products: caseStudy.products.map((product) => ({
        name: product.name,
        pristine: false
      })),
      isConfidential: caseStudy.confidential,
      image: getImage(),
      backgroundColor: 'primary',
      visibility: caseStudy.visibility
    };
  }

  /**
   * Normalize a GraphQl BusinessContext to a NormalizedBusinessContext
   * This mapper does not use a generated type as input since we need to use GraphQl for fetching the business context.
   *
   * @param businessContext
   */
  static normalizeContentfulBusinessContext(businessContext: BusinessContext): NormalizedBusinessContext {
    const defaultAsset: NormalizedAsset = {
      id: '',
      contentType: 'asset',
      description: '',
      title: '',
      url: '',
      width: null,
      height: null
    };

    const mapNavigationDropdown = (navigationDropdown: NavigationDropdown): NormalizedNavigationDropdown => ({
      id: get(navigationDropdown, 'sys.id', ''),
      contentType: 'navigationDropdown',
      icon: get(navigationDropdown, 'icon', null),
      label: get(navigationDropdown, 'label', ''),
      accessType: get(navigationDropdown, 'accessType', []),
      items: get(navigationDropdown, 'itemsCollection.items', []).map((item) => {
        // NavigationGroup
        if ('itemsCollection' in item) {
          return mapNavigationGroup(item);
        }

        // NavigationItem
        return mapNavigationItem(item);
      })
    });

    const mapNavigationGroup = (navigationGroup: NavigationGroup): NormalizedNavigationGroup => ({
      id: '',
      contentType: 'navigationGroup',
      label: get(navigationGroup, 'label', ''),
      accessType: get(navigationGroup, 'accessType', []),
      items: get(navigationGroup, 'itemsCollection.items', []).map(mapNavigationItem)
    });

    const mapNavigationItem = (navigationItem: NavigationItem): NormalizedNavigationItem => ({
      id: '',
      contentType: 'navigationItem',
      slug: get(navigationItem, 'slug', null),
      label: get(navigationItem, 'label', ''),
      icon: get(navigationItem, 'icon', null),
      identifier: get(navigationItem, 'identifier', null),
      description: get(navigationItem, 'description', null),
      externalUrl: get(navigationItem, 'externalUrl', null),
      accessType: get(navigationItem, 'accessType', []),
      openInNewTab: !!get(navigationItem, 'openInNewTab', false),
      queryParameters: get(navigationItem, 'queryParameters', null),
      page: get(navigationItem, 'page', null)
        ? ({
            contentType: get(navigationItem, 'page.__typename', ''),
            route: get(navigationItem, 'page.route', null) ?? get(navigationItem, 'page.slug', null)
          } as NormalizedPage)
        : null
    });

    return {
      id: get(businessContext, 'sys.id', ''),
      contentType: 'configuration',
      name: get(businessContext, 'name', ''),
      logo: {
        ...defaultAsset,
        title: get(businessContext, 'logo.title', ''),
        url: get(businessContext, 'logo.url', '')
      },
      favicon: {
        ...defaultAsset,
        title: get(businessContext, 'favicon.title', ''),
        url: get(businessContext, 'favicon.url', '')
      },
      header: get(businessContext, 'header', null)
        ? {
            id: get(businessContext, 'header.sys.id', ''),
            contentType: 'header',
            internalName: get(businessContext, 'header.internalName', ''),
            alignment: get(businessContext, 'header.alignment', null),
            navigationItems: get(businessContext, 'header.navigationItemsCollection.items', []).map((item) => {
              // NavigationDropdown
              if ('itemsCollection' in item) {
                return mapNavigationDropdown(item);
              }

              // NavigationItem
              return mapNavigationItem(item);
            })
          }
        : null,
      profileMenu: get(businessContext, 'profileMenu', null)
        ? {
            id: get(businessContext, 'profileMenu.sys.id', ''),
            contentType: 'header',
            internalName: get(businessContext, 'profileMenu.internalName', ''),
            alignment: get(businessContext, 'profileMenu.alignment', null),
            navigationItems: get(businessContext, 'profileMenu.navigationItemsCollection.items', []).map((item) => {
              // NavigationDropdown
              if ('itemsCollection' in item) {
                return mapNavigationDropdown(item);
              }

              // NavigationItem
              return mapNavigationItem(item);
            })
          }
        : null,
      footer: get(businessContext, 'footerCollection.items', []).map(mapNavigationGroup),
      footerDisabled: get(businessContext, 'footerDisabled', false),
      registrationLandingPage: businessContext.registrationLandingPage ? mapNavigationItem(businessContext.registrationLandingPage) : null,
      emailAddress: get(businessContext, 'emailAddress', null),
      phoneNr: get(businessContext, 'phoneNr', null),
      linkedInUrl: get(businessContext, 'linkedInUrl', null),
      address: get(businessContext, 'address', null),
      twitterSite: get(businessContext, 'twitterSite', null),
      backgroundImage: get(businessContext, 'backgroundImage.url', null)
        ? {
            ...defaultAsset,
            title: get(businessContext, 'backgroundImage.title', ''),
            url: get(businessContext, 'backgroundImage.url', '')
          }
        : null,
      placeholderImage: get(businessContext, 'placeholderImage.url', null)
        ? {
            ...defaultAsset,
            title: get(businessContext, 'placeholderImage.title', ''),
            url: get(businessContext, 'placeholderImage.url', '')
          }
        : null,
      uom: get(businessContext, 'uomCollection.items', []).map((item) => ({
        id: get(item, 'id', 'imperial'),
        contentType: 'unitOfMeasure',
        name: get(item, 'id', 'imperial'),
        value: get(item, 'value', 'unit-of-measure.imperial'),
        default: get(item, 'default', false)
      })),
      message: get(businessContext, 'message', null),
      messageTheme: get(businessContext, 'messageTheme', null),
      gtmId: get(businessContext, 'gtmId', null),
      googleMapsApiKey: get(businessContext, 'googleMapsApiKey', null),
      countryLanguageTable: get(businessContext, 'countryLanguageTable.countryLanguagePairsCollection.items', []).length
        ? {
            id: '',
            contentType: 'countryLanguageTable',
            internalName: '',
            countryLanguagePairs: get(businessContext, 'countryLanguageTable.countryLanguagePairsCollection.items', []).map(
              (item) =>
                ({
                  id: '',
                  contentType: 'countryLanguagePairs',
                  internalName: '',
                  country: {
                    id: '',
                    contentType: 'countryLocale',
                    name: '',
                    isoCode: get(item, 'country.shortName', '')
                  },
                  languages: get(item, 'languagesCollection.items', []).map((languageItem) => ({
                    id: '',
                    contentType: '',
                    fullName: '',
                    shortName: get(languageItem, 'shortName', '')
                  }))
                } as NormalizedCountryLanguagePair)
            )
          }
        : null,
      businessContextId: get(businessContext, 'businessContextId', null),
      systemPromptTranslator: null,
      excludedCountries: get(businessContext, 'excludedCountriesCollection.items', []).map((countryLocale) => ({
        id: '',
        contentType: 'countryLocale',
        name: get(countryLocale, 'fullName', ''),
        isoCode: get(countryLocale, 'shortName', '')
      }))
    };
  }

  /**
   * Normalize a Contentful Header to a NormalizedHeader
   *
   * @param header
   */
  static normalizeContentfulHeader(header: TypeHeader): NormalizedHeader {
    return {
      id: header.sys.id,
      contentType: header.sys.contentType.sys.id,
      internalName: header.fields.internalName,
      alignment: header.fields.alignment ?? null,
      navigationItems: header.fields.navigationItems.map((item) => {
        // TypeNavigationDropdownFields
        if (item.sys.contentType.sys.id === 'navigationDropdown') {
          return DataMapperUtils.normalizeContentfulNavigationDropdown(item as TypeNavigationDropdown);
        }

        // TypeNavigationItemFields
        return DataMapperUtils.normalizeContentfulNavigationItem(item as TypeNavigationItem);
      })
    };
  }

  /**
   * Normalize a Contentful NavigationGroup to a NormalizedNavigationGroup
   *
   * @param navigationGroup
   */
  static normalizeContentfulNavigationGroup(navigationGroup: TypeNavigationGroup): NormalizedNavigationGroup {
    return {
      id: navigationGroup.sys.id,
      contentType: navigationGroup.sys.contentType.sys.id,
      label: navigationGroup.fields.label,
      items: DataMapperUtils.normalizeContentfulCollection(navigationGroup.fields.items, DataMapperUtils.normalizeContentfulNavigationItem),
      accessType: (navigationGroup.fields.accessType as AccessLevel[]) ?? []
    };
  }

  /**
   * Normalize a Contentful NavigationItem to a NormalizedNavigationItem
   *
   * @param navigationItem
   */
  static normalizeContentfulNavigationItem(navigationItem: TypeNavigationItem): NormalizedNavigationItem {
    const normalizedPage = () => {
      if (!navigationItem.fields.page) return null;

      switch (navigationItem.fields.page.sys.contentType.sys.id) {
        case 'systemPage':
          return DataMapperUtils.normalizeContentfulSystemPage(navigationItem.fields.page as TypeSystemPage);
        case 'landingPage':
          return DataMapperUtils.normalizeContentfulLandingPage(navigationItem.fields.page as TypeLandingPage);
        case 'dynamicPage':
          return DataMapperUtils.normalizeContentfulDynamicPage(navigationItem.fields.page as TypeDynamicPage);
        case 'blogPost':
          return DataMapperUtils.normalizeContentfulBlogPost(navigationItem.fields.page as TypeBlogPost);
        default:
          return null;
      }
    };

    return {
      id: navigationItem.sys.id,
      contentType: navigationItem.sys.contentType.sys.id,
      label: navigationItem.fields.label,
      description: navigationItem.fields.description ?? null,
      icon: navigationItem.fields.icon ?? null,
      page: normalizedPage(),
      slug: navigationItem.fields.slug ?? null,
      queryParameters: navigationItem.fields.queryParameters ?? null,
      externalUrl: navigationItem.fields.externalUrl ?? null,
      openInNewTab: !!navigationItem.fields.openInNewTab,
      accessType: (navigationItem.fields.accessType as AccessLevel[]) ?? [],
      identifier: navigationItem.fields.identifier ?? null
    };
  }

  /**
   * Normalize a Contentful NavigationDropdown to a NormalizedNavigationDropdown
   *
   * @param navigationDropdown
   */
  static normalizeContentfulNavigationDropdown(navigationDropdown: TypeNavigationDropdown): NormalizedNavigationDropdown {
    return {
      id: navigationDropdown.sys.id,
      contentType: navigationDropdown.sys.contentType.sys.id,
      accessType: (navigationDropdown.fields.accessType as AccessLevel[]) ?? [],
      icon: navigationDropdown.fields.icon ?? null,
      label: navigationDropdown.fields.label,
      items: navigationDropdown.fields.items.map((item) => {
        // TypeNavigationGroupFields
        if (item.sys.contentType.sys.id === 'navigationGroup') {
          return DataMapperUtils.normalizeContentfulNavigationGroup(item as TypeNavigationGroup);
        }

        // TypeNavigationItemFields
        return DataMapperUtils.normalizeContentfulNavigationItem(item as TypeNavigationItem);
      })
    };
  }

  /**
   * Normalize a collection/array of Contentful items to a normalized variant.
   * This is a generic method that can be used to map multiple types.
   *
   * @param collection
   */
  static normalizeContentfulCollection<T>(collection: ContentfulCollection<unknown> | unknown[], mapper: (items: any) => void): T {
    if (Array.isArray(collection)) {
      return collection.map(mapper) as T;
    }

    return {
      ...collection,
      items: collection.items.map(mapper)
    } as T;
  }
}
